'use client'

import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import Button from '@ui/ButtonNew/Button'
import { Alert } from '@ui/kit'
import { login, sendVerificationCode, VerificationError } from '@view/auth/api'
import { authFormStore, type AuthTab } from '@view/auth/model'
import { deleteCookie } from 'cookies-next'
import { useForm, type SubmitHandler } from 'react-hook-form'
import * as yup from 'yup'

import { useAppStore } from '@/entities/app-store'
import { writeBrowserAuthMethodMarker } from '@/shared/analytics/auth-method-marker'
import { getFlowFailureContext } from '@/shared/analytics/events'
import { productAnalytics } from '@/shared/analytics/product-analytics'
import { checkEmail } from '@/shared/api/auth/check-email'
import { AuthTabs } from './auth-tabs'
import { authTexts } from './auth-texts'
import { ConsentCheckboxes } from './consent-checkboxes'
import { EmailPasswordFields } from './email-password-fields'
import { type AuthFormValues } from './form-types'
import { OrDivider } from './or-divider'
import { SocialButtons } from './social-buttons'
import { SwitchAuthCallout } from './switch-auth-callout'

const loginSchema = yup.object({
  email: yup.string().email('Не похоже на E-Mail').required('Необходимо заполнить'),
  password: yup.string().required('Пароль необходимо заполнить'),
})

const registerSchema = yup.object({
  email: yup
    .string()
    .email('Не похоже на E-Mail')
    .required('Необходимо заполнить')
    .test('unique-email', 'Такой E-Mail уже зарегистрирован', async (value) => {
      if (!value) {
        return true
      }
      return checkEmail(value)
    }),
  password: yup.string().required('Пароль необходимо заполнить'),
  terms: yup.boolean().oneOf([true], 'Необходимо принять Пользовательское соглашение'),
  personalData: yup.boolean().oneOf([true], 'Необходимо дать согласие на обработку персональных данных'),
  promo: yup.boolean(),
})

interface CredentialsFormProps {
  tab: AuthTab
  state: string
  redirectPage: string
  onSwitchTab: (tab: AuthTab) => void
}

export const CredentialsForm = ({ tab, state, redirectPage, onSwitchTab }: CredentialsFormProps) => {
  const appStore = useAppStore()
  const isRegister = tab === 'register'
  const otherTab: AuthTab = isRegister ? 'login' : 'register'

  const {
    register,
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<AuthFormValues>({
    mode: 'onBlur',
    criteriaMode: 'all',
    resolver: yupResolver(isRegister ? registerSchema : loginSchema) as never,
    defaultValues: {
      email: authFormStore.email,
      password: authFormStore.password,
      terms: authFormStore.consents.terms,
      personalData: authFormStore.consents.personalData,
      promo: authFormStore.consents.promo,
    },
  })

  useEffect(() => {
    const subscription = watch((values) => {
      authFormStore.setEmail(values.email ?? '')
      authFormStore.setPassword(values.password ?? '')
      authFormStore.setConsents({
        terms: values.terms ?? false,
        personalData: values.personalData ?? false,
        promo: values.promo ?? false,
      })
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const onSubmit: SubmitHandler<AuthFormValues> = async (values) => {
    if (isRegister) {
      await submitRegister(values)
    } else {
      await submitLogin(values)
    }
  }

  const submitRegister = async (values: AuthFormValues) => {
    productAnalytics.registrationStarted('auth_form')

    try {
      const { cooldownSeconds } = await sendVerificationCode(values.email)
      // Регистрацию отправляет экран ввода кода из письма, и данные он берёт из стора,
      // поэтому проверенные значения формы кладём туда до перехода на этот экран.
      authFormStore.setConsents({
        terms: values.terms,
        personalData: values.personalData,
        promo: values.promo,
      })
      authFormStore.startVerification(values.email, cooldownSeconds)
    } catch (error) {
      productAnalytics.flowFailed(error, {
        ...getFlowFailureContext(error),
        failureStage: 'request',
        flowId: 'registration',
      })
      setError('root.serverError', {
        type: 'server',
        message:
          error instanceof VerificationError ? error.message : 'Не удалось отправить письмо. Попробуйте ещё раз.',
      })
    }
  }

  // Вход: прямой запрос, код подтверждения не требуется.
  const submitLogin = async (values: AuthFormValues) => {
    try {
      await login({ email: values.email, password: values.password })
      writeBrowserAuthMethodMarker('password')
      await appStore.refresh()
      window.location.href = redirectPage
    } catch (error: any) {
      productAnalytics.flowFailed(error, {
        ...getFlowFailureContext(error),
        failureStage: 'authorization',
        flowId: 'authentication',
      })
      if (error?.status === 401) {
        setError('root.serverError', { type: 'server', message: error.response.data.errorText })
      } else {
        deleteCookie('auth-state')
        setError('root.serverError', { type: 'token', message: error.response.data.errorText })
      }
    }
  }

  const serverError = errors.root?.serverError
  const texts = authTexts[tab]

  return (
    <div>
      <AuthTabs active={tab} onChange={onSwitchTab} />

      <p className="mt-4 mb-4 text-center text-sm text-gray-500 dark:text-gray-400">{texts.subtitle}</p>

      <SocialButtons state={state} redirectPage={redirectPage} />

      <OrDivider />

      <form onSubmit={handleSubmit(onSubmit)}>
        {serverError?.type === 'server' && (
          <Alert view="error" className="mb-4">
            <p className="font-semibold">{serverError.message}</p>
            {!isRegister && <p className="text-sm">Если забыли, пароль можно восстановить.</p>}
          </Alert>
        )}

        {serverError?.type === 'token' && (
          <Alert view="error" className="mb-2">
            <p className="font-semibold">Произошла ошибка</p>
            <p className="text-sm">{serverError.message}</p>
          </Alert>
        )}

        <EmailPasswordFields register={register} errors={errors} showForgotPassword={!isRegister} />

        {isRegister && (
          <ConsentCheckboxes
            control={control}
            setValue={setValue}
            errors={{
              terms: errors.terms?.message?.toString(),
              personalData: errors.personalData?.message?.toString(),
            }}
          />
        )}

        <Button type="submit" view="primary" viewSize={'md'} fullWidth disabled={isSubmitting} className="mt-2">
          {texts.submit}
        </Button>
      </form>

      <SwitchAuthCallout tab={tab} onSwitch={() => onSwitchTab(otherTab)} />
    </div>
  )
}
